import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import {
  getPatientVitalFields,
  addVitalField,
  updateVitalField,
  deleteVitalField,
  VitalField,
  PatientVitalFields,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface PatientVitalFieldsManagementProps {
  skipLayout?: boolean;
}

const PatientVitalFieldsManagement: React.FC<PatientVitalFieldsManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [vitalFields, setVitalFields] = useState<PatientVitalFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ fieldKey: string; fieldLabel: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingField, setEditingField] = useState<VitalField | null>(null);
  const [formData, setFormData] = useState({
    fieldLabel: '',
    fieldType: 'TEXT' as 'TEXT' | 'NUMBER',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    fieldLabel?: string;
    fieldType?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVitalFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  const fetchVitalFields = async () => {
    try {
      setLoading(true);
      const tenantId = user?.isSystem ? undefined : user?.tenantId;
      const response = await getPatientVitalFields(tenantId);
      setVitalFields(response.data.data);
    } catch (err: any) {
      console.error('Failed to load patient vital fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (field?: VitalField) => {
    if (field) {
      setEditingField(field);
      setFormData({
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
      });
    } else {
      setEditingField(null);
      setFormData({
        fieldLabel: '',
        fieldType: 'TEXT',
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  /**
   * Format field label: capitalize first letter of each word, lowercase the rest
   * But allow all caps if user types all caps
   */
  const formatFieldLabel = (value: string): string => {
    if (!value) return value;
    
    // Remove any characters that aren't allowed (keep only letters, numbers, spaces, dashes)
    let cleaned = value.replace(/[^a-zA-Z0-9\s-]/g, '');
    
    // Replace multiple spaces with single space
    cleaned = cleaned.replace(/\s+/g, ' ');
    
    // Get the last word being typed (to preserve cursor position logic)
    const words = cleaned.split(' ');
    const lastWord = words[words.length - 1] || '';
    
    // Check if the last word being typed is all caps (user might be typing all caps)
    const isLastWordAllCaps = lastWord === lastWord.toUpperCase() && /[A-Z]/.test(lastWord) && lastWord.length > 1;
    
    // Check if entire input is all caps
    const isAllCaps = cleaned === cleaned.toUpperCase() && /[A-Z]/.test(cleaned) && cleaned.length > 1;
    
    if (isAllCaps || isLastWordAllCaps) {
      // Keep all caps if user typed all caps
      return cleaned;
    }
    
    // Otherwise, capitalize first letter of each word, lowercase the rest
    return cleaned
      .split(' ')
      .map((word) => {
        if (!word) return word;
        // Handle words with dashes
        if (word.includes('-')) {
          return word
            .split('-')
            .map((part) => {
              if (!part) return part;
              return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            })
            .join('-');
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const handleFieldLabelChange = (value: string) => {
    // Remove invalid characters as user types
    let cleaned = value.replace(/[^a-zA-Z0-9\s-]/g, '');
    
    // Replace multiple spaces with single space
    cleaned = cleaned.replace(/\s+/g, ' ');
    
    // Check for invalid characters (shouldn't happen after cleaning, but double-check)
    const invalidChars = /[^a-zA-Z0-9\s-]/;
    if (invalidChars.test(cleaned)) {
      setValidationErrors((prev) => ({
        ...prev,
        fieldLabel: 'Only letters, numbers, spaces, and dashes are allowed',
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.fieldLabel;
        return newErrors;
      });
    }
    
    // Format the value (capitalize first letter of each word)
    const formatted = formatFieldLabel(cleaned);
    
    setFormData((prev) => ({
      ...prev,
      fieldLabel: formatted,
    }));
  };

  const generateFieldKey = (fieldLabel: string): string => {
    return fieldLabel
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters (keep only alphanumeric, spaces, dashes)
      .replace(/[\s_-]+/g, '_') // Replace spaces, dashes, and underscores with single underscore
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    // Validation
    if (!formData.fieldLabel.trim()) {
      setValidationErrors({ fieldLabel: 'Field label is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    // Validate field label format
    const trimmed = formData.fieldLabel.trim();
    const allowedCharsRegex = /^[a-zA-Z0-9\s-]+$/;
    if (!allowedCharsRegex.test(trimmed)) {
      setValidationErrors({ fieldLabel: 'Field label can only contain letters, numbers, spaces, and dashes' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    // Check for double spaces
    if (trimmed.includes('  ')) {
      setValidationErrors({ fieldLabel: 'Field label cannot contain consecutive spaces' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    if (!formData.fieldType) {
      setValidationErrors({ fieldType: 'Field type is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    // Check for duplicate fieldKey and fieldLabel (excluding deleted fields)
    if (vitalFields && vitalFields.spec.fields) {
      const newFieldKey = generateFieldKey(trimmed);
      const normalizedLabel = trimmed.toLowerCase().trim();
      
      // Filter out deleted fields for validation
      const activeFields = vitalFields.spec.fields.filter((field) => !field.isDeleted);
      
      // Check for duplicate fieldKey
      const existingFieldByKey = activeFields.find(
        (field) => field.fieldKey === newFieldKey && (!editingField || field.fieldKey !== editingField.fieldKey)
      );

      if (existingFieldByKey) {
        setValidationErrors({ 
          fieldLabel: `A field with label "${existingFieldByKey.fieldLabel}" already exists with the same key. Please use a different field label.` 
        });
        setError('Please fix the highlighted errors.');
        setSubmitting(false);
        return;
      }

      // Check for duplicate fieldLabel (case-insensitive)
      const existingFieldByLabel = activeFields.find(
        (field) => field.fieldLabel.toLowerCase().trim() === normalizedLabel && (!editingField || field.fieldKey !== editingField.fieldKey)
      );

      if (existingFieldByLabel) {
        setValidationErrors({ 
          fieldLabel: `A field with label "${existingFieldByLabel.fieldLabel}" already exists. Please use a different field label.` 
        });
        setError('Please fix the highlighted errors.');
        setSubmitting(false);
        return;
      }
    }

    try {
      const tenantId = user?.isSystem ? undefined : user?.tenantId;

      if (editingField) {
        // Update existing field (use existing fieldKey)
        await updateVitalField(
          editingField.fieldKey,
          formData.fieldLabel.trim(),
          formData.fieldType,
          tenantId
        );
      } else {
        // Add new field (Field key will be auto-generated on backend)
        await addVitalField(
          formData.fieldLabel.trim(),
          formData.fieldType,
          tenantId
        );
      }

      await fetchVitalFields();
      setShowModal(false);
      setFormData({
        fieldLabel: '',
        fieldType: 'TEXT',
      });
      setEditingField(null);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to save vital field';
      setError(errorMsg);
      console.error('Failed to save vital field:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!fieldToDelete) return;

    try {
      const tenantId = user?.isSystem ? undefined : user?.tenantId;
      await deleteVitalField(fieldToDelete.fieldKey, tenantId);
      await fetchVitalFields();
      setShowDeleteConfirm(false);
      setFieldToDelete(null);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to delete vital field';
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setShowDeleteConfirm(false);
      console.error('Failed to delete vital field:', err);
    }
  };

  const handleDeleteClick = (field: VitalField) => {
    setFieldToDelete({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel,
    });
    setShowDeleteConfirm(true);
  };

  const handleRefresh = () => {
    fetchVitalFields();
  };

  const getFieldTypeBadge = (fieldType: string) => {
    const badges: Record<string, { text: string; style: React.CSSProperties }> = {
      TEXT: {
        text: 'TEXT',
        style: {
          background: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
          color: isDark ? '#93c5fd' : '#1e40af',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
        },
      },
      NUMBER: {
        text: 'NUMBER',
        style: {
          background: isDark ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5',
          color: isDark ? '#6ee7b7' : '#065f46',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
        },
      },
    };

    return badges[fieldType] || badges.TEXT;
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Patient Vital Fields Management</h2>
        <p>Manage custom fields for capturing patient vital information</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Patient Vital Fields</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('patientvitalfield.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Field
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading patient vital fields...</div>
        ) : !vitalFields || vitalFields.spec.fields.length === 0 ? (
          <div className="empty-state">
            <h3>No vital fields configured yet</h3>
            <p>Create your first vital field to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 400px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Field Label</th>
                    <th>Field Key</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vitalFields.spec.fields.map((field, index) => {
                    const badge = getFieldTypeBadge(field.fieldType);
                    return (
                      <tr key={index}>
                        <td>
                          <strong>{field.fieldLabel}</strong>
                        </td>
                        <td>
                          <code style={{ 
                            background: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                            color: isDark ? '#e5e7eb' : '#374151',
                            padding: '2px 6px', 
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'monospace'
                          }}>
                            {field.fieldKey}
                          </code>
                        </td>
                        <td>
                          <span style={badge.style}>{badge.text}</span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {hasPermission('patientvitalfield.update') && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenModal(field)}
                              >
                                Edit
                              </button>
                            )}
                            {hasPermission('patientvitalfield.delete') && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDeleteClick(field)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingField ? 'Edit Vital Field' : 'Create New Vital Field'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Field Label
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fieldLabel}
                    onChange={(e) => handleFieldLabelChange(e.target.value)}
                    className={validationErrors.fieldLabel ? 'input-error' : ''}
                    placeholder="e.g., Temperature, Blood Pressure"
                  />
                  {validationErrors.fieldLabel && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.fieldLabel}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    Only letters, numbers, spaces, and dashes are allowed.
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Field Type
                    <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.fieldType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fieldType: e.target.value as 'TEXT' | 'NUMBER',
                      }))
                    }
                    className={validationErrors.fieldType ? 'input-error' : ''}
                  >
                    <option value="TEXT">TEXT</option>
                    <option value="NUMBER">NUMBER</option>
                  </select>
                  {validationErrors.fieldType && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.fieldType}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    The data type for this field. TEXT for text input, NUMBER for numeric values.
                  </small>
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingField ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingField ? 'Update' : 'Create'} Field`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Vital Field"
        message={`Are you sure you want to delete the field "${fieldToDelete?.fieldLabel}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setFieldToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="info"
        onConfirm={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
        onCancel={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default PatientVitalFieldsManagement;
